import { startSignUpWithEmail } from "actions/authFunctions"
import AddressInput, { AddressType } from "components/utils/AddressInput"
import InputField from "components/utils/InputField"
import ProgressBar from "components/utils/ProgressBar"
import Select, { SelectProps } from "components/utils/Select"
import GetpackedLoginButton from "components/utils/loginButtons/GetpackedLoginButton"
import { useDocument } from "hooks/firestore/core/useDocument"
import { getShortUrlRef } from "hooks/firestore/getRefs"
import { useDisplayToast } from "hooks/misc/useDisplayToast"
import { useRouter } from "next/router"
import { useMemo, useState } from "react"
import Shop from "types/firestore/shop"
import { createNewShop } from "util-functions/cloud-functions/createNewShop"
import sanitizeUrl from "util-functions/sanitizeUrl"
import zxcvbn from "zxcvbn"

export type SignupShopInfo = {
  firstName: string
  lastName: string
  name: string
  type: string
  otherType?: string
  referralSource?: string
  otherReferralSource?: string
  email: string
}

// interface ShopSignUpFormProps {}

const shopTypes: SelectProps<string>["options"] = [
  { label: "Bäckerei", value: "bakery" },
  { label: "Hofladen", value: "farmersmarket" },
  { label: "Gastronomie", value: "restaurant" },
  { label: "Lebensmittelgeschäft", value: "shop" },
  { label: "Metzgerei", value: "butcher" },
  { label: "Sonstige", value: "others" },
]

const referralSources: SelectProps<string>["options"] = [
  { label: "Internet", value: "internet" },
  { label: "Hofdirekt", value: "hofdirekt" },
  { label: "Instagram", value: "instagram" },
  { label: "Freunde oder Familie", value: "friends or family" },
  { label: "Kollegen", value: "colleagues" },
  { label: "Zeitungen oder Magazin", value: "newspaper or magazine" },
  { label: "Kunden", value: "customers" },
  { label: "Andere", value: "other" },
]

const ShopSignUpForm = () => {
  const [shopInfo, setShopInfo] = useState<Partial<SignupShopInfo>>({
    email: "",
    firstName: "",
    lastName: "",
    name: "",
    type: "",
    otherType: "",
    otherReferralSource: "",
    referralSource: "",
  })
  const [errors, setErrors] = useState<string[]>([])

  const [address, setAddress] = useState<Partial<AddressType>>({})
  const [passwordA, setPasswordA] = useState("")
  const [passwordB, setPasswordB] = useState("")

  const [loading, setLoading] = useState(false)

  const router = useRouter()
  const ratingResult = useMemo(() => (passwordA ? zxcvbn(passwordA) : undefined), [passwordA])

  const displayToast = useDisplayToast()

  const shorthand = sanitizeUrl(shopInfo.name ?? "")
  const existingShorthand = useDocument(getShortUrlRef(shorthand))
  const shorthandAlreadyExists = !!existingShorthand?.document
  const tenRandomCharacters = useMemo(() => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
    let result = ""
    for (let i = 10; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)]
    return result
  }, [])
  const newShopShorturl = shorthandAlreadyExists ? tenRandomCharacters : shorthand

  const data = {
    ...shopInfo,
    zip: address.zip,
    city: address.city,
    address: address.street ? `${address.street}${address.number ? ` ${address.number}` : ""}` : undefined,
  }

  const passwordError =
    ratingResult && ratingResult?.score < 3 ? "strength" : passwordA && passwordA !== passwordB ? "mismatch" : undefined

  const newErrors = [
    ...(!data.name ? ["name"] : []),
    ...(!data.type ? ["type"] : []),
    ...(!data.firstName ? ["firstName"] : []),
    ...(!data.lastName ? ["lastName"] : []),
    ...(!data.address ? ["address"] : []),
    ...(!data.zip ? ["zip"] : []),
    ...(!data.city ? ["city"] : []),
    ...(!data.email ? ["email"] : []),
    ...(!data.email?.includes("@") ? ["email"] : []),
    ...(!passwordA ? ["passwordA"] : []),
    ...(!passwordB ? ["passwordB"] : []),
  ]

  const [duplicateEmailError, setDuplicateEmailError] = useState(false)

  const error = passwordError || (newErrors.length ? "data" : duplicateEmailError ? "duplicateEmail" : undefined)

  const errorMessage =
    error &&
    {
      duplicateEmail: "Es existiert schon ein Account zu dieser E-Mail",
      strength: "Das Passwort ist zu schwach",
      mismatch: "Die Passwörter stimmen nicht überein",
      data: "Bitte fülle alle Felder aus",
    }[error]

  const handleSignUp = async () => {
    if (newErrors.length > 0) {
      setErrors(newErrors)
      displayToast("Bitte fülle alle Felder aus", "error")
      return
    }
    if (errorMessage) {
      displayToast(errorMessage, "error")
      return
    }
    setErrors([])
    const fullData = data as Required<typeof data>
    setDuplicateEmailError(false)

    setLoading(true)
    try {
      const user = {
        email: fullData.email,
        password: passwordA,
      }
      await startSignUpWithEmail(user, "admin/dashboard")

      const shopContact: Shop["contact"] = {
        zipCode: fullData.zip ?? "",
        website: "",
        address: fullData.address ?? "",
        registerTown: fullData.city ?? "",
        city: fullData.city ?? "",
        representativeFirstName: fullData.firstName,
        companyName: fullData.name,
        representativeLastName: fullData.lastName,
        phone: "",
        vatNum: "",
        registerNum: "",
        email: fullData.email,
        country: "DE",
      }

      // ref url param
      const refUrlParam = (router.query.ref as string | undefined) || ""

      await createNewShop({
        name: fullData.name,
        type: fullData.type,
        shorthand: newShopShorturl,
        contact: shopContact,
        otherType: fullData.otherType,
        referralSource: fullData.referralSource,
        otherReferralSource: fullData.otherReferralSource,
        refUrlParam: refUrlParam,
      })

      await router.push("/email-verification")
      setLoading(false)
    } catch (error) {
      if (error) {
        console.error(error)
        //TODO: Adjust for signup errors, this is a login error
        setDuplicateEmailError((error as Error).message.includes("auth/user-not-found") ? true : false)
      }
      setLoading(false)
    }
  }

  return (
    <>
      <style jsx>{`
        @keyframes slidein {
          from {
            max-height: 0px;
            opacity: 0;
          }

          to {
            max-height: 24px;
            opacity: 1;
          }
        }
        .errorMessage {
          margin: 0;
          max-height: 24px;
          opacity: 1;
          animation: slidein 0.15s linear;
        }
      `}</style>
      <InputField
        danger={errors.includes("name") && !shopInfo.name}
        type="text"
        value={shopInfo.name ?? ""}
        onChange={e => setShopInfo({ ...shopInfo, name: e.target.value })}
        name="organization"
        autoComplete="organization"
      >
        Name deines Betriebs
      </InputField>
      <Select
        label="Art des Betriebs"
        options={shopTypes}
        value={shopInfo.type ?? ""}
        onChange={e => {
          setShopInfo({ ...shopInfo, type: e || undefined })
          setErrors(errors.filter(error => error !== "type"))
        }}
        error={errors.includes("type")}
      />
      {shopInfo.type === "others" && (
        <InputField
          type="text"
          value={shopInfo.otherType ?? ""}
          onChange={e => setShopInfo({ ...shopInfo, otherType: e.target.value })}
          name="otherType"
        >
          Art/Branche des Betriebs
        </InputField>
      )}

      <Select
        label="Wie hast du von uns erfahren?"
        options={referralSources}
        value={shopInfo.referralSource ?? ""}
        onChange={e => {
          setShopInfo({ ...shopInfo, referralSource: e || "" })
          setErrors(errors.filter(error => error !== "type"))
        }}
      />
      {shopInfo.referralSource === "other" && (
        <InputField
          type="text"
          value={shopInfo.otherReferralSource ?? ""}
          onChange={e => setShopInfo({ ...shopInfo, otherReferralSource: e.target.value })}
          name="otherReferralSource"
        >
          Wie hast du von uns erfahren?
        </InputField>
      )}

      <style jsx>{`
        .nameRow {
          display: flex;
          flex-wrap: nowrap;
          gap: 10px;
          justify-self: stretch;

          & > :global(div) {
            flex-grow: 1;
          }
        }
      `}</style>
      <InputField
        danger={errors.includes("email")}
        type="email"
        value={shopInfo.email ?? ""}
        onChange={e => {
          setShopInfo({ ...shopInfo, email: e.target.value })
          setErrors(errors.filter(error => error !== "email"))
        }}
        name="email"
        autoComplete="email"
      >
        E-Mail Adresse
      </InputField>
      <div className="nameRow">
        <InputField
          danger={errors.includes("firstName") && !shopInfo.firstName}
          type="text"
          value={shopInfo.firstName ?? ""}
          onChange={e => {
            setShopInfo({ ...shopInfo, firstName: e.target.value })
            setErrors(errors.filter(error => error !== "firstName"))
          }}
          name="name"
          autoComplete="given-name"
        >
          Vorname
        </InputField>
        <InputField
          danger={errors.includes("lastName") && !shopInfo.lastName}
          type="text"
          value={shopInfo.lastName ?? ""}
          onChange={e => {
            setShopInfo({ ...shopInfo, lastName: e.target.value })
            setErrors(errors.filter(error => error !== "lastName"))
          }}
          name="name"
          autoComplete="family-name"
        >
          Nachname
        </InputField>
      </div>
      <AddressInput address={address} setAddress={setAddress} errors={errors} setErrors={setErrors} />

      <InputField
        danger={errors.includes("passwordA")}
        type="password"
        value={passwordA}
        onChange={e => {
          setPasswordA(e.target.value)
          setErrors(errors.filter(error => error !== "passwordA"))
        }}
        name="password"
        autoComplete="new-password"
      >
        Passwort
      </InputField>
      <InputField
        danger={errors.includes("passwordB")}
        type="password"
        value={passwordB}
        onChange={e => {
          setPasswordB(e.target.value)
          setErrors(errors.filter(error => error !== "passwordB"))
        }}
        name="password"
        autoComplete="new-password"
      >
        Passwort wiederholen
      </InputField>

      <ProgressBar percentage={(ratingResult?.score || 0.2) * 25} dangerBelow={25} yellowBelow={50} />
      {errorMessage ? (
        <p className="errorMessage" style={{ background: "white" }}>
          {errorMessage}
        </p>
      ) : null}
      <label style={{ maxWidth: "450px", background: "white", fontSize: "0.9rem", textAlign: "center" }}>
        Durch die Registrierung stimmst du den{" "}
        <a href="https://getpacked.de/agb/" target="_blank" rel="noreferrer">
          Allgemeinen Geschäftsbedingungen (AGB)
        </a>{" "}
        und der{" "}
        <a href="https://getpacked.de/rechtliches/datenschutzerklaerung-shop-backend/" target="_blank" rel="noreferrer">
          Datenschutzerklärung
        </a>{" "}
        zu.
      </label>
      <p
        style={{
          fontSize: "20px",
          marginTop: "24px",
          maxWidth: "450px",
          textAlign: "center",
        }}
      >
        Benötigt ihr Hilfe bei der Anmeldung oder beim Erstellen eures Shops? Bucht einen Termin{" "}
        <a href="https://calendly.com/getpacked/30min" target="_blank" rel="noreferrer">
          hier.
        </a>
      </p>
      <GetpackedLoginButton onClick={handleSignUp} disabled={!!errors.length || loading} loading={loading} signUp />
    </>
  )
}

export default ShopSignUpForm
